import { Dialog } from "@kobalte/core/dialog";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { type Component, For, Show } from "solid-js";
import { useI18n } from "~/i18n/context";

interface DuplicateFileErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  filenames: string[];
  /** Asset IDs matching the duplicates. When present (length > 0), a "Use in Chat" CTA renders that calls onUseInChat. */
  assetIds?: string[];
  onUseInChat?: (assetIds: string[]) => void;
}

/**
 * Modal component for displaying duplicate file upload errors.
 * Shows a list of all skipped files so the user can dismiss once.
 */
export const DuplicateFileErrorModal: Component<DuplicateFileErrorModalProps> = (props) => {
  const { t } = useI18n();

  return (
    <AnimateChildren show={props.isOpen} enter={{ opacity: [0, 1], y: [10, 0] }} exit={{ opacity: [1, 0], y: [0, 10] }}>
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) props.onClose();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed inset-0 z-120 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed inset-0 z-120 flex items-center justify-center">
              <Dialog.Content class="relative max-h-[80vh] w-screen max-w-md overflow-y-auto rounded-xl bg-panel p-6 text-primary-text">
                <div class="mb-6">
                  <h3 class="mb-4 text-lg font-medium">
                    {props.filenames.length === 1
                      ? t("error_duplicate_file")
                      : t("error_duplicate_files", { count: String(props.filenames.length) })}
                  </h3>
                  <div class="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/20">
                    <div class="flex gap-2">
                      <span class="shrink-0 text-lg">⚠️</span>
                      <div class="min-w-0 flex-1">
                        <Show
                          when={props.filenames.length === 1}
                          fallback={
                            <p class="text-sm text-amber-800 dark:text-amber-200">
                              {t("error_duplicate_files_in_knowledge", { count: String(props.filenames.length) })}
                            </p>
                          }
                        >
                          <p class="text-sm text-amber-800 dark:text-amber-200">
                            {t("error_duplicate_file_in_knowledge", { filename: props.filenames[0] })}
                          </p>
                        </Show>
                        <Show when={props.filenames.length > 1}>
                          <ul class="mt-2 list-inside list-disc text-xs text-amber-700 dark:text-amber-300">
                            <For each={props.filenames}>{(filename) => <li class="truncate">{filename}</li>}</For>
                          </ul>
                        </Show>
                      </div>
                    </div>
                  </div>
                  <p class="mt-3 text-sm text-secondary-text">
                    {props.filenames.length === 1
                      ? t("error_duplicate_file_instruction")
                      : t("error_duplicate_files_instruction")}
                  </p>
                </div>

                <div class="flex items-center justify-end gap-3">
                  <Show
                    when={props.onUseInChat && (props.assetIds?.length ?? 0) > 0}
                    fallback={
                      <StButton onClick={props.onClose} class="bg-brand px-4 py-2 text-on-brand hover:bg-brand-hover">
                        {t("common.close")}
                      </StButton>
                    }
                  >
                    <StButton onClick={props.onClose} class="px-4 py-2 text-secondary-text hover:text-primary-text">
                      {t("common.close")}
                    </StButton>
                    <StButton
                      onClick={() => {
                        props.onUseInChat?.(props.assetIds ?? []);
                        props.onClose();
                      }}
                      class="bg-brand px-4 py-2 text-on-brand hover:bg-brand-hover"
                    >
                      {t("error_duplicate_use_existing_in_chat")}
                    </StButton>
                  </Show>
                </div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
